/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RecreateSWMDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HandleApiActions, labCar } from "../../../../redux/actions";
import typeApprovalAPI from "../../../../redux/actions/typeApprovalAPI";
import ruleEngineActions from "../../../../redux/actions/ruleEngineAction";
import { Box, DialogContent, Stack, Typography } from "@mui/material";
import CustomDialogBoxV2 from "../../common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../../common/select/CustomSelectV2";
import MultiselectConnectorV2 from "../../common/connectors/MultiselectConnectorV2";
import { componentStyle } from "../../../../config/appTheme";
import CustomButtonV2 from "../../common/button/CustomButtonV2";
import { useDispatch } from "react-redux";
import UsersMultiselect from "../../../labcar/UsersMultiselect";
import PropTypes from "prop-types";

function RecreateSWMDialog({
  openSWModuleReplaceDialog,
  setOpenSWModuleReplaceDialog,
  selectedSWModuleReplaceDetails,
  setSelectedSWModuleReplaceDetails,
  setIsLinkSoftwareModulesScreen,
  selectedZone,
  selectedEEComponentDetails,
  getSWModulesList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  variantOptionList,
  title,
  supplierOptionsList,
  recreationParentSWMList,
  selectedParentSWMDetails,
  setSelectedParentSWMDetails,
}) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [nameError] = useState("");
  const [version, setVersion] = useState("");
  const [supplierName, setSupplierName] = useState(null);
  const [versionError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError] = useState("");
  const [summary, setSummary] = useState("");
  const [summaryError] = useState("");
  const [selectedVfIds, setSelectedVfIds] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [discp, setDiscp] = useState("");
  const [selectedSWM, setSelectedSWM] = useState(null);

  const [actionType1DialogOpen, setActionType1DialogOpen] = useState(false);
  const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);
  const githubLink = "";

  const [showIntersectionImpactPrompt, setShowIntersectionImpactPrompt] =
    useState(false);
  const [actionType2DialogOpen, setActionType2DialogOpen] = useState(false);

  const [showRuleSetListDialog, setShowRuleSetListDialog] = useState(false);
  const [ruleSetList, setRuleSetList] = useState([]);
  const [selectedRuleSet, setSelectedRuleSet] = useState("");
  const [ruleSetTree, setRuleSetTree] = useState(null);
  const [rulePath, setRulePath] = useState([]);
  const [selectedVariantsManySW, setSelectedVariantsManySW] = useState([]);

  const [coOwners, setCoOwners] = useState([]);
  const [editors, setEditors] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showRuleSetQuestionDialog, setShowRuleSetQuestionDialog] =
    useState(false);

  const dispatch = useDispatch();

  const handleAnswerAt = (idx, answer) => {
    if (!rulePath || idx < 0 || idx >= rulePath?.length) return;

    const newPath = rulePath
      ?.slice(0, idx + 1)
      ?.map((entry, i) => (i === idx ? { ...entry, answer } : entry));

    const currentEntry = newPath[idx];
    const nextBranch = currentEntry?.node?.data?.[`${answer}_branch`];

    if (nextBranch) {
      newPath.push({ node: nextBranch, answer: null });
      setRulePath(newPath);
    } else if (currentEntry?.node?.data?.category) {
      if (currentEntry?.node?.data?.requires_homologation) {
        replaceChangesAndClose();
      }
      setRulePath(newPath);
      setShowRuleSetQuestionDialog(false);
      setRuleSetTree(null);
      setRulePath([]);
      setSelectedRuleSet("");
    } else {
      setRulePath(newPath);
    }
  };

  useEffect(() => {
    if (openSWModuleReplaceDialog && selectedSWModuleReplaceDetails) {
      setName(selectedSWModuleReplaceDetails?.name || "");
      setVersion(selectedSWModuleReplaceDetails?.version || "");
      setDescription(selectedSWModuleReplaceDetails?.description || "");
      setSupplierName(selectedSWModuleReplaceDetails?.supplier_name || null);
      setSummary(selectedSWModuleReplaceDetails?.summary || "");
      setSelectedVfIds(
        (selectedSWModuleReplaceDetails?.vf_ids || [])
          ?.map(Number)
          ?.filter((vfId) => !Number.isNaN(vfId)),
      );
      setDiscp(selectedSWModuleReplaceDetails?.reason || "");
    }
  }, [openSWModuleReplaceDialog, selectedSWModuleReplaceDetails]);

  const clearReplaceSWModuleFields = () => {
    setName("");
    setVersion("");
    setDescription("");
    setSummary("");
    setSelectedVfIds([]);
    setDiscp("");
    setSelectedVariantsManySW([]);
    setSupplierName(null);
  };

  const isReplaceSWModuleValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
    const isNameChanged =
      name?.trim() !== selectedSWModuleReplaceDetails?.name;
    const isVersionChanged =
      version?.trim() !== selectedSWModuleReplaceDetails?.version;
    const isDescChanged =
      description?.trim() !== selectedSWModuleReplaceDetails?.description;
    const isSummaryChanged =
      summary?.trim() !== selectedSWModuleReplaceDetails?.summary;
    const isVfChanged =
      selectedVfIds?.length !== selectedSWModuleReplaceDetails?.vf_ids?.length;

    const isDetailsChanged =
      isNameChanged ||
      isVersionChanged ||
      isDescChanged ||
      isSummaryChanged ||
      isVfChanged;

    return (
      !nameError &&
      !versionError &&
      !descriptionError &&
      selectedVfIds?.length > 0 &&
      regexMin3.test(version?.trim()) &&
      isDetailsChanged &&
      (name?.trim()?.length ?? 0) >= 3 &&
      (version?.trim()?.length ?? 0) >= 3 &&
      (description?.trim()?.length ?? 0) >= 3 &&
      (discp?.trim()?.length ?? 0) >= 3 &&
      (summary?.trim()?.length === 0 || !summaryError) &&
      (summary?.trim()?.length === 0 || (summary?.trim()?.length ?? 0) >= 3) &&
      (githubLink?.trim()?.length === 0 || (githubLink?.trim()?.length ?? 0) >= 3)
    );
  };

  const isUpdateDraftSWComponentValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
    return (
      !nameError &&
      !versionError &&
      !descriptionError &&
      selectedVfIds?.length > 0 &&
      (name?.trim()?.length ?? 0) >= 3 &&
      (supplierName?.length ?? 0) > 0 &&
      version?.trim() !== "" &&
      (version?.trim()?.length ?? 0) >= 3 &&
      !versionError &&
      regexMin3.test(version?.trim()) &&
      description?.trim() !== "" &&
      (description?.trim()?.length ?? 0) >= 3 &&
      (summary?.trim()?.length === 0 || !summaryError) &&
      (summary?.trim()?.length === 0 || (summary?.trim()?.length ?? 0) >= 3) &&
      (githubLink?.trim()?.length === 0 || (githubLink?.trim()?.length ?? 0) >= 3) &&
      !isLoading
    );
  };

  const handleCloseSWModuleReplace = () => {
    setOpenSWModuleReplaceDialog(false);
    clearReplaceSWModuleFields();
    setSelectedSWModuleReplaceDetails(null);
    setShowConfirmation(false);
    setShowIntersectionImpactPrompt(false);
  };

  const handleRecreateClick = async () => {
    const reqObj = {
      supplier_name: supplierName,
      current_time: new Date().toISOString(),
      project_id: Number(selectedProjectDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedEEComponentDetails?.ee_id),
      variants_ids: selectedEEComponentDetails?.variant_ids,
      version: version?.trim(),
      save_as_draft: false,
      name: name?.trim(),
      ...(summary && { summary }),
      description,
      vf_ids: selectedVfIds,
      parent_project_id: selectedProjectDetails?.parent_project_id,
      parent_swm_id: selectedSWModuleReplaceDetails?.swm_id,
      co_owners: coOwners?.map((data) => data?.value),
      edit_users: editors?.map((data) => data?.value),
      view_users: viewers?.map((data) => data?.value),
    };

    setIsLoading(true);

    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          api_name: "/RCS/swm/recreate",
          https_method: "POST",
          action_type: "",
          description: "Recreate SWM Model",
          params: reqObj,
          show_toast: true,
        }),
      ),
    );

    if (!resp?.error) {
      setOpenSWModuleReplaceDialog(false);
      clearReplaceSWModuleFields();
    }

    await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...labCar.get_sw_modules,
          params: {
            project_id: Number(selectedProjectDetails?.project_id),
            variant_id: Number(selectedSingleVariantDetails?.variant_id),
            ee_id: Number(selectedEEComponentDetails?.ee_id),
            zone: selectedZone,
          },
          show_toast: false,
        }),
      ),
    );
    setIsLoading(false);

    handleCloseSWModuleReplace();
  };

  const handleActionType1Dialogpen = (swmDetails) => {
    setSelectedSWM(swmDetails);
    setActionType1DialogOpen(true);
  };

  const handleActionType2DialogOpen = (swmDetails) => {
    setSelectedSWM(swmDetails);
    setActionType2DialogOpen(true);
  };

  const confirmReplaceAndRedirect = async ({
    selectedSWModuleReplaceDetails: detailsParam,
    willImptIntrOnly = false,
  }) => {
    if (detailsParam?.swm_id) {
      const reqObj = {
        ...(supplierName && { supplier_name: supplierName }),
        current_time: new Date().toISOString(),
        project_id: Number(detailsParam?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(detailsParam?.ee_id),
        swm_id: Number(detailsParam?.swm_id),
        variants_ids: selectedEEComponentDetails?.variant_ids,
        version: version?.trim(),
        name: name?.trim(),
        ...(summary && { summary }),
        description,
        vf_ids: selectedVfIds,
        will_it_impact: true,
        reason: discp,
      };
      try {
        const response = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...labCar.replace_sw_module,
              params: reqObj,
              show_toast: true,
            }),
          ),
        );
        if (!response.error) {
          handleCloseSWModuleReplace();
          if (willImptIntrOnly) {
            handleActionType2DialogOpen(detailsParam);
            await Promise.resolve(
              dispatch(
                HandleApiActions({
                  ...labCar.update_action_type,
                  params: {
                    current_time: new Date().toISOString(),
                    project_id: Number(detailsParam?.project_id),
                    variant_id: Number(selectedSingleVariantDetails?.variant_id),
                    ee_id: Number(detailsParam?.ee_id),
                    swm_id: Number(detailsParam?.swm_id),
                    is_dependency_linking: true,
                  },
                  show_toast: false,
                }),
              ),
            );
          } else {
            handleActionType1Dialogpen(detailsParam);
          }
          getSWModulesList();
        }
      } catch (error) {
        console.error("Error in confirmReplaceAndRedirect:", error);
      }
    }
  };

  const handleCloseSWModuleReplaceDialog = () => {
    setOpenSWModuleReplaceDialog(false);
  };

  const replaceChangesAndClose = async () => {
    const lastNode = rulePath[rulePath?.length - 1]?.node;
    const requiresHomologation =
      lastNode?.data?.requires_homologation || false;
    if (selectedSWModuleReplaceDetails?.swm_id) {
      const reqObj = {
        supplier_name: supplierName,
        current_time: new Date().toISOString(),
        project_id: Number(selectedSWModuleReplaceDetails?.project_id),
        variant_id: Number(selectedSingleVariantDetails?.variant_id),
        ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
        swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
        variant_ids: selectedEEComponentDetails?.variant_ids,
        name: name?.trim(),
        ...(summary && { summary }),
        description,
        vf_ids: selectedVfIds,
        version: version?.trim(),
        will_it_impact: false,
        requires_homologation: requiresHomologation,
        reason: discp,
        git_link: githubLink,
      };
      try {
        const response = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...labCar.replace_sw_module,
              params: reqObj,
              show_toast: true,
            }),
          ),
        );
        if (!response.error) {
          handleCloseSWModuleReplace();
        }
        if (response?.data?.snow_resp) {
          setTimeout(() => {
            setTicketsDialogOpen(true);
          }, 200);
        }
        await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...labCar.get_sw_modules,
              params: {
                project_id: Number(selectedSWModuleReplaceDetails?.project_id),
                ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
                variant_id: Number(selectedSingleVariantDetails?.variant_id),
                zone: selectedZone,
              },
              show_toast: false,
            }),
          ),
        );
      } catch (error) {
        console.error("Error in replaceChangesAndClose:", error);
      }
    }
  };

  const updateChangesWithoutStandardAndClose = async () => {
    const reqObj = {
      ...(supplierName && { supplier_name: supplierName }),
      current_time: new Date().toISOString(),
      project_id: Number(selectedSWModuleReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
      swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
      variants_ids: selectedEEComponentDetails?.variant_ids,
      version: version?.trim(),
      name: name?.trim(),
      ...(summary && { summary }),
      description,
      vf_ids: selectedVfIds,
      will_it_impact: true,
      reason: discp,
    };

    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.replace_sw_module,
            params: reqObj,
            show_toast: true,
          }),
        ),
      );

      if (!response?.error) {
        handleCloseSWModuleReplace();
      }

      if (response?.data?.snow_resp) {
        setTimeout(() => {
          setTicketsDialogOpen(true);
        }, 200);
      }

      await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...labCar.get_sw_modules,
            params: {
              project_id: Number(selectedSWModuleReplaceDetails?.project_id),
              ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
              variant_id: Number(selectedSingleVariantDetails?.variant_id),
              zone: selectedZone,
            },
            show_toast: false,
          }),
        ),
      );
    } catch (error) {
      console.error("Error in updateChangesWithoutStandardAndClose:", error);
    }
  };

  const checkForStandardWithRuleSet = async () => {
    const reqObj = {
      project_id: Number(selectedSWModuleReplaceDetails?.project_id),
      variant_id: Number(selectedSingleVariantDetails?.variant_id),
      ee_id: Number(selectedSWModuleReplaceDetails?.ee_id),
      swm_id: Number(selectedSWModuleReplaceDetails?.swm_id),
      tickets_type: 1,
    };

    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...typeApprovalAPI.get_tickets,
            params: reqObj,
            show_toast: false,
          }),
        ),
      );
      handleCloseSWModuleReplaceDialog();
      setShowIntersectionImpactPrompt(false);

      const standardsWithRuleSet = [
        ...new Set(
          Array.isArray(response?.data)
            ? response.data
                ?.map((ticket) => ticket?.ls_details?.standard_number)
                ?.filter(Boolean)
            : [],
        ),
      ];

      if (standardsWithRuleSet?.length > 0) {
        const standardWithRuleSet = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...ruleEngineActions.get_rule_engine_with_standards,
              params: { standard: standardsWithRuleSet },
              show_toast: false,
            }),
          ),
        );

        if (
          standardWithRuleSet?.data &&
          (Array.isArray(standardWithRuleSet.data)
            ? standardWithRuleSet?.data?.length > 0
            : Object.keys(standardWithRuleSet?.data)?.length > 0)
        ) {
          setShowRuleSetListDialog(true);
          setRuleSetList(standardWithRuleSet?.data);
        } else {
          updateChangesWithoutStandardAndClose();
        }
        return standardWithRuleSet.data || [];
      }
      replaceChangesAndClose();
      return [];
    } catch (error) {
      console.error("Error in checkForStandardWithRuleSet:", error);
      return false;
    }
  };

  const handleRuleSetListDialogClose = () => {
    setShowRuleSetListDialog(false);
    setSelectedRuleSet("");
    handleCloseSWModuleReplace();
  };

  const viewRuleSetQuestions = async (ruleSetIdParam) => {
    const ruleSetIdToUse = ruleSetIdParam || selectedRuleSet;
    try {
      const response = await Promise.resolve(
        dispatch(
          HandleApiActions({
            ...ruleEngineActions.get_rule_set_tree,
            params: {
              rule_set_id: ruleSetIdToUse,
            },
            show_toast: false,
          }),
        ),
      );

      if (response?.data) {
        setRuleSetTree(response.data);
        const root = response.data?.tree
          ? { type: "question", data: response.data.tree }
          : null;
        if (root) {
          setRulePath([{ node: root, answer: null }]);
          setShowRuleSetQuestionDialog(true);
        }
      }
    } catch (error) {
      console.error("Error in viewRuleSetQuestions:", error);
    }
  };

  const handleActionType1DialogClose = () => {
    setActionType1DialogOpen(false);
  };

  const handleActionType2DialogClose = () => {
    setActionType2DialogOpen(false);
  };

  const handleTicketsDialogClose = () => {
    setTicketsDialogOpen(false);
  };

  useEffect(() => {
    if (
      openSWModuleReplaceDialog &&
      selectedSingleVariantDetails?.variant_id != null
    ) {
      const id = selectedSingleVariantDetails.variant_id;
      setSelectedVariantsManySW((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const hasId = prevArr?.map(String).includes(String(id));
        if (hasId) return prevArr;
        return [id, ...prevArr];
      });
    }
  }, [openSWModuleReplaceDialog, selectedSingleVariantDetails]);

  const isFormDisabled =
    title === "Replace Software Module"
      ? !isReplaceSWModuleValid()
      : !isUpdateDraftSWComponentValid();

  const successButtonText =
    title === "Replace Software Module" ? "Replace" : "Add";

  return (
    <Box>
      <CustomDialogBoxV2
        open={openSWModuleReplaceDialog}
        onClose={handleCloseSWModuleReplace}
        title={title}
        onDisabled={isFormDisabled}
        successButtonName={successButtonText}
        rejectButtonName={"Cancel"}
        onClickSuccess={handleRecreateClick}
        onClickReject={handleCloseSWModuleReplace}
        actionButtonStyle={"continuous"}
      >
        <Box sx={{ width: "450px" }}>
          <Stack spacing={2} mb={2}>
            <CustomSelectV2
              placeholder={"Select Parent EE Component"}
              label="Parent SWM Component"
              required={true}
              selectedValue={selectedParentSWMDetails}
              handleOnChange={(e) => {
                setSelectedParentSWMDetails(e.target.value);
              }}
              optionsList={recreationParentSWMList.map((item) => ({
                label: item?.name,
                value: item,
              }))}
            />
            <CustomFormTextFieldV2
              size={"small"}
              label={"Number"}
              required={true}
              value={name}
              setValue={(value) => value?.length <= 40 && setName(value)}
              maxLength={40}
            />
            <CustomFormTextFieldV2
              size={"small"}
              label={"Version"}
              required={true}
              type={"text"}
              fldType={"textR"}
              value={version}
              setValue={(value) => value?.length <= 40 && setVersion(value)}
              maxLength={40}
            />
            <CustomFormTextFieldV2
              size={"small"}
              label={"Description"}
              required={true}
              value={description}
              setValue={(value) =>
                value?.length <= 500 && setDescription(value)
              }
              maxLength={500}
            />
            <CustomFormTextFieldV2
              size={"small"}
              label={"Summary"}
              value={summary}
              setValue={(value) => value?.length <= 250 && setSummary(value)}
              maxLength={250}
            />

            <CustomSelectV2
              size={"small"}
              label={"Select Supplier Name"}
              optionsList={supplierOptionsList}
              selectedValue={supplierName || " "}
              handleOnChange={(e) => setSupplierName(e.target.value)}
            />

            <MultiselectConnectorV2
              required
              size={"small"}
              label={"Select Vehicle Functions"}
              optionsList={selectedSingleVariantDetails?.vehicle_function_details?.map(
                (el) => ({
                  value: el.id,
                  label: `${el.abbreviation} - ${el.name}`,
                }),
              )}
              selectedValues={selectedVfIds || " "}
              setSelectedValues={setSelectedVfIds}
              placeholder={false}
            />
            <UsersMultiselect
              coOwnerDetails={selectedParentSWMDetails?.co_owner_details || []}
              editorDetails={selectedParentSWMDetails?.edit_user_details || []}
              viewerDetails={selectedParentSWMDetails?.view_user_details || []}
              coOwners={coOwners}
              setCoOwners={setCoOwners}
              editors={editors}
              setEditors={setEditors}
              viewers={viewers}
              setViewers={setViewers}
              owner={[selectedParentSWMDetails?.created_by]}
            />

            {title === "Recreate Software Module" ? (
              <MultiselectConnectorV2
                disabled={true}
                size={"small"}
                multiple
                selectedValues={selectedVariantsManySW || " "}
                setSelectedValues={setSelectedVariantsManySW}
                label={"Select Variants"}
                optionsList={variantOptionList}
                placeholder={false}
                immutableValues={
                  selectedSingleVariantDetails?.variant_id == null
                    ? []
                    : [selectedSingleVariantDetails?.variant_id]
                }
              />
            ) : null}

            {title === "Replace Software Module" ? (
              <CustomFormTextFieldV2
                size={"small"}
                label={"Reason"}
                required={true}
                value={discp}
                setValue={(value) => value?.length <= 100 && setDiscp(value)}
                maxLength={100}
              />
            ) : null}

            <CustomDialogBoxV2
              open={showConfirmation}
              title={"Action"}
              onClose={() => {
                setShowConfirmation(false);
              }}
              dialogActions={
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <CustomButtonV2
                    size="small"
                    type="cancel"
                    btnName="No"
                    handleOnClick={() => {
                      setShowConfirmation(false);

                      setTimeout(() => {
                        setShowIntersectionImpactPrompt(true);
                      }, 200);
                    }}
                  />
                  <CustomButtonV2
                    size="small"
                    type="submit"
                    btnName="Yes"
                    handleOnClick={() =>
                      confirmReplaceAndRedirect({
                        selectedSWModuleReplaceDetails,
                      })
                    }
                  />
                </Box>
              }
            >
              <Box
                sx={{
                  width: "350px",
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "15px",
                  }}
                >
                  Will it impact the existing dependencies?
                </Typography>
              </Box>
            </CustomDialogBoxV2>

            <CustomDialogBoxV2
              open={showIntersectionImpactPrompt}
              onClose={() => {
                setShowIntersectionImpactPrompt(false);
              }}
              title={"Action"}
              dialogActions={
                <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <CustomButtonV2
                    size="small"
                    type="cancel"
                    btnName="No"
                    handleOnClick={checkForStandardWithRuleSet}
                  />
                  <CustomButtonV2
                    size="small"
                    type="submit"
                    btnName="Yes"
                    handleOnClick={() =>
                      confirmReplaceAndRedirect({
                        selectedSWModuleReplaceDetails,
                        willImptIntrOnly: true,
                      })
                    }
                  />
                </Box>
              }
            >
              <Box
                sx={{
                  width: "350px",
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ ...componentStyle.font.text }}>
                  Will it impact the existing intersection?
                </Typography>
              </Box>
            </CustomDialogBoxV2>
          </Stack>
        </Box>
      </CustomDialogBoxV2>

      <Box>
        <CustomDialogBoxV2
          open={actionType1DialogOpen}
          onClose={handleActionType1DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            setIsLinkSoftwareModulesScreen(true);
            navigate(
              `/dashboard/v2/link?swm_id=${
                selectedSWM?.swm_id
              }&ee_id=${
                selectedEEComponentDetails?.ee_id
              }&selectedZone=${
                selectedZone?.zone_position_name
              }&tabFromurl=${1}`,
            );
          }}
          onClickReject={handleActionType1DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>
              Software module dependency linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>

        <CustomDialogBoxV2
          open={actionType2DialogOpen}
          onClose={handleActionType2DialogClose}
          title={"Action Required"}
          onClickSuccess={() => {
            navigate(
              `/dashboard/v2/type-approval/link?ee_id=${
                selectedEEComponentDetails?.ee_id
              }&zone=${
                selectedZone?.zone_position_name
              }&swm_id=${
                selectedSWM?.swm_id
              }&tab=${3}`,
            );
            setActionType2DialogOpen(false);
          }}
          onClickReject={handleActionType2DialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Link Now"}
          rejectButtonName={"Link Later"}
        >
          <Stack sx={{ width: "350px", height: "50px" }} spacing={2}>
            <Typography>
              Software module intersection linking is pending!
            </Typography>
          </Stack>
        </CustomDialogBoxV2>

        <CustomDialogBoxV2
          open={ticketsDialogOpen}
          onClose={handleTicketsDialogClose}
          title={"Tickets Recreated"}
          dialogActions={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <CustomButtonV2
                btnName="Close"
                handleOnClick={handleTicketsDialogClose}
                type="cancel"
              />
              <CustomButtonV2
                btnName="View"
                handleOnClick={() => {
                  navigate(`/dashboard/v2/type-approval/tickets?tab=${1}`);
                  setTicketsDialogOpen(false);
                }}
              />
            </Box>
          }
        >
          <Box
            sx={{
              width: "350px",
              height: "50px",
              alignItems: "center",
              display: "flex",
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
              }}
            >
              Successfully created the tickets for the intersections!
            </Typography>
          </Box>
        </CustomDialogBoxV2>

        <CustomDialogBoxV2
          open={showRuleSetListDialog}
          onClose={handleRuleSetListDialogClose}
          title="Select Rule Set"
          onClickSuccess={async () => {
            await viewRuleSetQuestions(selectedRuleSet);
            setShowRuleSetListDialog(false);
          }}
          onDisabled={!selectedRuleSet}
          onClickReject={handleRuleSetListDialogClose}
          actionButtonStyle={"continuous"}
          successButtonName={"Confirm"}
          rejectButtonName={"Cancel"}
        >
          <Stack spacing={2} sx={{ width: "350px", height: "50px" }}>
            <CustomSelectV2
              size={"small"}
              label={"Select Rule Set"}
              required={true}
              optionsList={
                ruleSetList?.map((rs) => ({
                  value: rs?.rule_set_id || rs?.id,
                  label: rs?.name,
                })) || []
              }
              selectedValue={selectedRuleSet || " "}
              handleOnChange={(e) => setSelectedRuleSet(e.target.value)}
            />
          </Stack>
        </CustomDialogBoxV2>

        <CustomDialogBoxV2
          open={showRuleSetQuestionDialog}
          onClose={() => {
            setShowRuleSetQuestionDialog(false);
            setRuleSetTree(null);
            setRulePath([]);
            setSelectedRuleSet("");
          }}
          title={`Rule Set: ${ruleSetTree?.name || ""}`}
          contentSx={{ minHeight: "350px", width: "450px" }}
        >
          <DialogContent>
            <Stack spacing={2}>
              <Stack spacing={1}>
                {rulePath?.map((entry, idx) => {
                  const isCategory = entry.node?.type === "category";
                  const keyString =
                    entry.node?.data?.text || `rule-path-${idx + 1}`;
                  return (
                    <Box
                      key={keyString}
                      sx={{
                        border: "1px solid #e0e0e0",
                        p: 1,
                        borderRadius: 1,
                        backgroundColor:
                          idx === rulePath?.length - 1 ? "#fafafa" : "white",
                      }}
                    >
                      {isCategory ? (
                        <Stack>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Session Ended: {entry.node?.data?.description}
                          </Typography>
                        </Stack>
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {entry.node?.data?.text}
                            </Typography>
                          </Box>

                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Box>
                              <CustomButtonV2
                                btnName={"Yes"}
                                handleOnClick={() => handleAnswerAt(idx, "yes")}
                                type={
                                  entry.answer === "yes" ? "submit" : "cancel"
                                }
                              />
                            </Box>
                            <Box>
                              <CustomButtonV2
                                btnName={"No"}
                                handleOnClick={() => handleAnswerAt(idx, "no")}
                                type={
                                  entry.answer === "no" ? "submit" : "cancel"
                                }
                              />
                            </Box>
                          </Box>
                        </Stack>
                      )}
                    </Box>
                  );
                })}

                {rulePath?.length > 0 &&
                rulePath[rulePath?.length - 1]?.node?.type === "category" ? (
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    mt={2}
                  >
                    <CustomButtonV2
                      type="submit"
                      btnName={"Finish"}
                      handleOnClick={() => {
                        replaceChangesAndClose();
                        setShowRuleSetQuestionDialog(false);
                        setRuleSetTree(null);
                        setRulePath([]);
                        setSelectedRuleSet("");
                        handleCloseSWModuleReplace();
                      }}
                    />
                  </Stack>
                ) : null}
              </Stack>
            </Stack>
          </DialogContent>
        </CustomDialogBoxV2>
      </Box>
    </Box>
  );
}

RecreateSWMDialog.propTypes = {
  title: PropTypes.string,
  openSWModuleReplaceDialog: PropTypes.bool,
  setOpenSWModuleReplaceDialog: PropTypes.func,
  setSelectedSWModuleReplaceDetails: PropTypes.func,
  setIsLinkSoftwareModulesScreen: PropTypes.func,
  setSelectedParentSWMDetails: PropTypes.func,
  getSWModulesList: PropTypes.func,
  variantOptionList: PropTypes.array,
  supplierOptionsList: PropTypes.array,
  recreationParentSWMList: PropTypes.array,
  selectedSWModuleReplaceDetails: PropTypes.object,
  selectedZone: PropTypes.object,
  selectedEEComponentDetails: PropTypes.object,
  selectedProjectDetails: PropTypes.object,
  selectedSingleVariantDetails: PropTypes.object,
  selectedParentSWMDetails: PropTypes.object,
};

export default RecreateSWMDialog;
